import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserAnalyses, deleteAnalysis, downloadRawFile, formatFileSize } from '../utils/fileStorage';
import { X, FileText, Download, Trash2, Calendar, BarChart3, AlertCircle, Loader2, ChevronLeft, Cloud } from 'lucide-react';

const AccountSettings = ({ onClose, onLoadAnalysis }) => {
  const { currentUser } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUserAnalyses();
  }, []);

  const loadUserAnalyses = async () => {
    try {
      setLoading(true);
      setError('');
      const userAnalyses = await getUserAnalyses(currentUser.uid);
      setAnalyses(userAnalyses);
    } catch (err) {
      console.error('Error loading analyses:', err);
      setError('Failed to load your analyses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (analysis) => {
    if (!window.confirm(`Are you sure you want to delete "${analysis.fileName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(analysis.id);
      await deleteAnalysis(analysis.id, analysis.rawFilePath, analysis.analysisPath);
      setAnalyses(analyses.filter(a => a.id !== analysis.id));
    } catch (err) {
      console.error('Error deleting analysis:', err);
      alert('Failed to delete analysis. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  const handleDownload = async (analysis) => {
    try {
      setDownloading(analysis.id);
      await downloadRawFile(analysis.rawFileURL, analysis.fileName);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const handleLoadForAnalysis = (analysis) => {
    if (onLoadAnalysis) {
      onLoadAnalysis(analysis);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 px-6 py-5 flex items-center justify-between border-b-4 border-indigo-500">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 hover:bg-indigo-800/50 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-white">My Files</h2>
              <p className="text-sm text-indigo-200">Manage your genetic analyses</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-indigo-800/50 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Account Info */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Signed in as</p>
              <p className="font-semibold text-slate-800">{currentUser.email}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600">Total Analyses</p>
              <p className="text-2xl font-bold text-indigo-600">{analyses.length}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 240px)' }}>
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
              <p className="text-slate-600">Loading your analyses...</p>
            </div>
          ) : analyses.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No analyses yet</h3>
              <p className="text-slate-500">Upload a genetic data file to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">
                Your Genetic Analyses
              </h3>
              
              {analyses.map((analysis) => (
                <div
                  key={analysis.id}
                  className="bg-white border border-slate-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                        <h4 className="font-semibold text-slate-800 truncate">
                          {analysis.fileName}
                        </h4>
                        
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          <Cloud className="w-3 h-3 mr-1" />
                          {analysis.compressionRatio}% compressed
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {analysis.uploadedAt?.toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-slate-600">
                          <BarChart3 className="w-4 h-4" />
                          <span>
                            {formatFileSize(analysis.originalSize)} → {formatFileSize(analysis.compressedSize)}
                          </span>
                        </div>
                      </div>

                      {/* Analysis Summary */}
                      {analysis.stats && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-slate-500">Total SNPs</p>
                              <p className="font-semibold text-slate-800">
                                {analysis.stats.totalSnps?.toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-slate-500">Analyzed</p>
                              <p className="font-semibold text-indigo-600">
                                {analysis.stats.analyzedSnps?.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleLoadForAnalysis(analysis)}
                        className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                      >
                        Load Analysis
                      </button>
                      
                      <button
                        onClick={() => handleDownload(analysis)}
                        disabled={downloading === analysis.id}
                        className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {downloading === analysis.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        Download
                      </button>
                      
                      <button
                        onClick={() => handleDelete(analysis)}
                        disabled={deleting === analysis.id}
                        className="px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {deleting === analysis.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">
              🔒 Files are compressed and encrypted. Analysis data loads instantly.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;